import React, { useState, useEffect, useCallback } from "react";
import axios from "../api/Axios";
import DragDropUpload from "../components/DragDropUpload";

// Step 1: Identification
const Step1Identification = ({
  step1Data,
  handleStep1Change,
  validationErrors,
  setValidationErrors,
  nextStep,
  loading,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleUploadStart,
  validateDocuments
}) => {
  const [isIdentityVerified, setIsIdentityVerified] = useState(false);
  const [checkingIdentityStatus, setCheckingIdentityStatus] = useState(true);
  const [rejectionComments, setRejectionComments] = useState('');

  // Check if user already has verified identity document
  useEffect(() => {
    const checkIdentityVerification = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          setCheckingIdentityStatus(false);
          return;
        }

        const response = await axios.get(`/accounts/tutor-docs/?user=${userId}`);
        const documents = response.data;
        
        // Check if there's a verified Identity Verification document
        const hasVerifiedIdentity = documents.some(doc => 
          doc.doc_type === 'Identity Verification' && doc.doc_status === 'Verified'
        );
        
        // Only check for rejected documents if there's NO verified document
        let rejectedDoc = null;
        if (!hasVerifiedIdentity) {
          rejectedDoc = documents.find(doc => 
            doc.doc_type === 'Identity Verification' && 
            doc.doc_status === 'Rejected' && 
            doc.comments && 
            doc.comments.trim() !== ''
          );
        }
        
        setIsIdentityVerified(hasVerifiedIdentity);
        setRejectionComments(rejectedDoc ? rejectedDoc.comments : '');
      } catch (error) {
        console.error('Error checking identity verification status:', error);
      } finally {
        setCheckingIdentityStatus(false);
      }
    };

    checkIdentityVerification();
  }, []);
  const handleNext = async () => {
    const errors = {};
    if (!step1Data.surname) errors.surname = 'Surname is required';
    if (!step1Data.other_names) errors.other_names = 'Other names are required';
    if (!step1Data.citizenship) errors.citizenship = 'Citizenship is required';
    // Only validate ID document type if identity is not already verified
    if (!isIdentityVerified && !step1Data.id_document_type) errors.id_document_type = 'ID Document Type is required';
    if (!step1Data.id_number) errors.id_number = 'ID number is required';
    if (!step1Data.phone_number) {
      errors.phone_number = 'Phone number is required';
    } else if (step1Data.phone_number.length < 8) {
      errors.phone_number = 'Please enter a valid phone number';
    }
    if (!step1Data.gender) errors.gender = 'Gender is required';
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    // Validate documents only if identity is not already verified
    if (!isIdentityVerified) {
      const documentsValid = validateDocuments();
      if (!documentsValid) {
        document.getElementById("Required Documents")?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        return;
      }
    }
    setValidationErrors({});
    nextStep();
  };

  return (
    <div>
      <h3>Identification</h3>

      {/* Rejection Comments Warning */}
      {rejectionComments && (
        <div className="alert alert-warning d-flex align-items-start mb-3" role="alert">
          <svg className="bi flex-shrink-0 me-2 mt-1" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div>
            <strong>Identity Verification Issue</strong>
            <p className="mb-0">{rejectionComments}</p>
          </div>
        </div>
      )}

      {/* Surname and Other Names - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.surname ? 'is-invalid' : ''}`}
              type="text"
              id="surnameInput"
              placeholder="Surname"
              value={step1Data.surname}
              readOnly
            />
            <label htmlFor="surnameInput">Surname</label>
            {validationErrors.surname && (
              <div className="invalid-feedback">{validationErrors.surname}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.other_names ? 'is-invalid' : ''}`}
              type="text"
              id="otherNamesInput"
              placeholder="Other Names"
              value={step1Data.other_names}
              readOnly
            />
            <label htmlFor="otherNamesInput">Other Names</label>
            {validationErrors.other_names && (
              <div className="invalid-feedback">{validationErrors.other_names}</div>
            )}
          </div>
        </div>
      </div>

      {/* Gender and Citizenship - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.gender ? 'is-invalid' : ''}`}
              id="genderSelect"
              value={step1Data.gender || ''}
              onChange={(e) => handleStep1Change('gender', e.target.value)}
              disabled={!!step1Data.gender}
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            <label htmlFor="genderSelect">Gender</label>
            {validationErrors.gender && (
              <div className="invalid-feedback">{validationErrors.gender}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.citizenship ? 'is-invalid' : ''}`}
              id="citizenshipSelect"
              value={step1Data.citizenship}
              onChange={(e) => handleStep1Change('citizenship', e.target.value)}
            >
              <option value="">Select Citizenship</option>
              <option value="South Africa">South Africa</option>
              <option value="Afghanistan">Afghanistan</option>
              <option value="Albania">Albania</option>
              <option value="Algeria">Algeria</option>
              <option value="Angola">Angola</option>
              <option value="Argentina">Argentina</option>
              <option value="Australia">Australia</option>
              <option value="Austria">Austria</option>
              <option value="Bangladesh">Bangladesh</option>
              <option value="Belgium">Belgium</option>
              <option value="Botswana">Botswana</option>
              <option value="Brazil">Brazil</option>
              <option value="Canada">Canada</option>
              <option value="China">China</option>
              <option value="Democratic Republic of Congo">Democratic Republic of Congo</option>
              <option value="Egypt">Egypt</option>
              <option value="Eswatini">Eswatini</option>
              <option value="Ethiopia">Ethiopia</option>
              <option value="France">France</option>
              <option value="Germany">Germany</option>
              <option value="Ghana">Ghana</option>
              <option value="India">India</option>
              <option value="Indonesia">Indonesia</option>
              <option value="Italy">Italy</option>
              <option value="Japan">Japan</option>
              <option value="Kenya">Kenya</option>
              <option value="Lesotho">Lesotho</option>
              <option value="Malawi">Malawi</option>
              <option value="Malaysia">Malaysia</option>
              <option value="Mozambique">Mozambique</option>
              <option value="Namibia">Namibia</option>
              <option value="Netherlands">Netherlands</option>
              <option value="Nigeria">Nigeria</option>
              <option value="Pakistan">Pakistan</option>
              <option value="Philippines">Philippines</option>
              <option value="Poland">Poland</option>
              <option value="Portugal">Portugal</option>
              <option value="Russia">Russia</option>
              <option value="Rwanda">Rwanda</option>
              <option value="Saudi Arabia">Saudi Arabia</option>
              <option value="Spain">Spain</option>
              <option value="Sweden">Sweden</option>
              <option value="Switzerland">Switzerland</option>
              <option value="Tanzania">Tanzania</option>
              <option value="Turkey">Turkey</option>
              <option value="Uganda">Uganda</option>
              <option value="Ukraine">Ukraine</option>
              <option value="United Arab Emirates">United Arab Emirates</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="United States">United States</option>
              <option value="Zambia">Zambia</option>
              <option value="Zimbabwe">Zimbabwe</option>
              <option value="Other">Other</option>
            </select>
            <label htmlFor="citizenshipSelect">Citizenship</label>
            {validationErrors.citizenship && (
              <div className="invalid-feedback">{validationErrors.citizenship}</div>
            )}
          </div>
        </div>
      </div>


      {/* ID Document Type and ID Number - Horizontally aligned */}
      {!checkingIdentityStatus && (
        <div className="row mb-3">
          {!isIdentityVerified && (
            <div className="col-md-6">
              <div className="form-floating mb-3">
                <select
                  className={`form-select ${validationErrors.id_document_type ? 'is-invalid' : ''}`}
                  id="idDocumentTypeSelect"
                  value={step1Data.id_document_type || ''}
                  onChange={e => handleStep1Change('id_document_type', e.target.value)}
                >
                  <option value="">Select ID Document Type</option>
                  <option value="National ID">National ID</option>
                  <option value="Passport">Passport</option>
                  <option value="Driver's License">Driver's License</option>
                  <option value="Refugee Travel Document">Refugee Travel Document</option>
                </select>
                <label htmlFor="idDocumentTypeSelect">ID Document Type</label>
                {validationErrors.id_document_type && (
                  <div className="invalid-feedback">{validationErrors.id_document_type}</div>
                )}
              </div>
            </div>
          )}
          <div className={isIdentityVerified ? "col-md-12" : "col-md-6"}>
            <div className="form-floating mb-3">
              <input
                className={`form-control ${validationErrors.id_number ? 'is-invalid' : ''}`}
                type="text"
                id="idNumberInput"
                placeholder="ID Number"
                value={step1Data.id_number}
                onChange={e => handleStep1Change('id_number', e.target.value)}
              />
              <label htmlFor="idNumberInput">ID / Passport Number</label>
              {validationErrors.id_number && (
                <div className="invalid-feedback">{validationErrors.id_number}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Number - Full width */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.phone_number ? 'is-invalid' : ''}`}
          type="tel"
          id="phoneNumberInput"
          placeholder="Phone Number"
          value={step1Data.phone_number || ''}
          onChange={(e) => handleStep1Change('phone_number', e.target.value)}
        />
        <label htmlFor="phoneNumberInput">Phone Number</label>
        {validationErrors.phone_number && (
          <div className="invalid-feedback">{validationErrors.phone_number}</div>
        )}
      </div>

      <h3>Required Document</h3>
      <div id="Required Documents" className="row g-3 mb-3">
        {checkingIdentityStatus ? (
          <div className="text-center text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
            Checking identity verification status...
          </div>
        ) : isIdentityVerified ? (
          <div className="alert alert-success d-flex align-items-center" role="alert">
            <svg className="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Success:">
              <use xlinkHref="#check-circle-fill"/>
            </svg>
            <div>
              <strong>✓ Identity Verified</strong>
              <p className="mb-0">Your identity document has already been verified. You can proceed to the next step.</p>
            </div>
          </div>
        ) : (
          <DragDropUpload
            documentType="id_passport"
            title="Photo of Identity/Passport Document"
            onDocumentUploaded={handleDocumentUploaded}
            onDocumentUploadFailed={handleDocumentUploadFailed}
            uploadedFilenames={uploadedFilenames}
            isUploading={uploadingDocuments.has('id_passport')}
            onUploadStart={handleUploadStart}
            hasError={documentErrors.has('id_passport')}
            uploadEndpoint="/accounts/tutor-docs/"
          />
        )}
      </div>

      <button
        className="btn btn-primary mt-4"
        onClick={handleNext}
        disabled={loading}
      >
        {loading ? 'Submitting...' : 'Submit & Continue'}
      </button>

      <style>
        {`
          h3 {
            font-size: 22px;
            color: #495057;
            font-weight: 600;
            margin: 2rem 0 1.5rem 0;
            padding: 0.75rem 0;
            border-bottom: 2px solid #e9ecef;
            position: relative;
          }
          
          h3::before {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 60px;
            height: 2px;
            background: linear-gradient(45deg, #0d6efd, #6610f2);
            border-radius: 1px;
          }
          
          h3:first-child {
            margin-top: 0;
          }
          
          .doc-card {
            transition: box-shadow 0.2s, transform 0.3s ease;
            animation: fadeInSlide 0.4s ease-in-out;
          }
          .doc-card:hover {
            box-shadow: 0 0 0.5rem #0d6efd33;
            background: #f8f9fa;
            transform: translateY(-2px);
          }
          
          @keyframes fadeInSlide {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </div>
  );
};

// Step 2: Address
const Step2Address = ({
  step2Data,
  handleStep2Change,
  validationErrors,
  setValidationErrors,
  previousStep,
  nextStep,
  loading,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  setDocumentErrors,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleUploadStart
}) => {
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [checkingAddressStatus, setCheckingAddressStatus] = useState(true);
  const [rejectionComments, setRejectionComments] = useState('');

  // Check if user already has verified proof of address document
  useEffect(() => {
    const checkAddressVerification = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          setCheckingAddressStatus(false);
          return;
        }

        const response = await axios.get(`/accounts/tutor-docs/?user=${userId}`);
        const documents = response.data;
        
        // Check if there's a verified Proof of Address document
        const hasVerifiedAddress = documents.some(doc => 
          doc.doc_type === 'Proof of Address' && doc.doc_status === 'Verified'
        );
        
        // Only check for rejected documents if there's NO verified document
        let rejectedDoc = null;
        if (!hasVerifiedAddress) {
          rejectedDoc = documents.find(doc => 
            doc.doc_type === 'Proof of Address' && 
            doc.doc_status === 'Rejected' && 
            doc.comments && 
            doc.comments.trim() !== ''
          );
        }
        
        setIsAddressVerified(hasVerifiedAddress);
        setRejectionComments(rejectedDoc ? rejectedDoc.comments : '');
      } catch (error) {
        console.error('Error checking address verification status:', error);
      } finally {
        setCheckingAddressStatus(false);
      }
    };

    checkAddressVerification();
  }, []);
  const handleNext = async () => {
    const errors = {};
    
    if (!step2Data.province) errors.province = 'Province is required';
    if (!step2Data.addresslines) errors.addresslines = 'Address is required';
    if (!step2Data.suburb) errors.suburb = 'Suburb is required';
    if (!step2Data.city) errors.city = 'City is required';
    if (!step2Data.postcode) errors.postcode = 'Postal code is required';
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Validate proof of address document only if not already verified
    if (!isAddressVerified && !uploadedDocuments.has('proof_of_address')) {
      setDocumentErrors(new Set(['proof_of_address']));
      document.querySelector('[data-document="proof_of_address"]')?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      return;
    }
    
    setValidationErrors({});
    setDocumentErrors(new Set());
    nextStep();
  };

  return (
    <div className="container">
      <h3>Current Address</h3>

      {/* Rejection Comments Warning */}
      {rejectionComments && (
        <div className="alert alert-warning d-flex align-items-start mb-3" role="alert">
          <svg className="bi flex-shrink-0 me-2 mt-1" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div>
            <strong>Proof of Address Issue</strong>
            <p className="mb-0">{rejectionComments}</p>
          </div>
        </div>
      )}

      {/* Country - Full width - Locked to South Africa */}
      <div className="form-floating mb-3">
        <input
          className="form-control"
          type="text"
          id="countryInput"
          value="South Africa"
          readOnly
          style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
        />
        <label htmlFor="countryInput">Country</label>
      </div>

      {/* Province - Full width */}
      <div className="form-floating mb-3">
        <select
          className={`form-select ${validationErrors.province ? 'is-invalid' : ''}`}
          id="provinceInput"
          value={step2Data.province}
          onChange={(e) => handleStep2Change('province', e.target.value)}
        >
          <option value="">Select Province</option>
          <option value="Eastern Cape">Eastern Cape</option>
          <option value="Free State">Free State</option>
          <option value="Gauteng">Gauteng</option>
          <option value="KwaZulu-Natal">KwaZulu-Natal</option>
          <option value="Limpopo">Limpopo</option>
          <option value="Mpumalanga">Mpumalanga</option>
          <option value="Northern Cape">Northern Cape</option>
          <option value="North West">North West</option>
          <option value="Western Cape">Western Cape</option>
        </select>
        <label htmlFor="provinceInput">Province</label>
        {validationErrors.province && (
          <div className="invalid-feedback">{validationErrors.province}</div>
        )}
      </div>

      {/* Address Lines - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating">
            <input
              className={`form-control ${validationErrors.addresslines ? 'is-invalid' : ''}`}
              type="text"
              id="addressLine1Input"
              placeholder="Address Line 1"
              value={step2Data.addresslines || ''}
              onChange={(e) => handleStep2Change('addresslines', e.target.value)}
            />
            <label htmlFor="addressLine1Input">Address Line 1</label>
            {validationErrors.addresslines && (
              <div className="invalid-feedback">{validationErrors.addresslines}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating">
            <input
              className="form-control"
              type="text"
              id="addressLine2Input"
              placeholder="Address Line 2 (Optional)"
              value={step2Data.addresslines2 || ''}
              onChange={(e) => handleStep2Change('addresslines2', e.target.value)}
            />
            <label htmlFor="addressLine2Input">Address Line 2 (Optional)</label>
          </div>
        </div>
      </div>

      {/* Suburb and City - Horizontally aligned */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className="form-floating">
            <input
              className={`form-control ${validationErrors.suburb ? 'is-invalid' : ''}`}
              type="text"
              id="suburbInput"
              placeholder="Suburb"
              value={step2Data.suburb || ''}
              onChange={(e) => handleStep2Change('suburb', e.target.value)}
            />
            <label htmlFor="suburbInput">Suburb</label>
            {validationErrors.suburb && (
              <div className="invalid-feedback">{validationErrors.suburb}</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating">
            <input
              className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
              type="text"
              id="cityInput"
              placeholder="City"
              value={step2Data.city || ''}
              onChange={(e) => handleStep2Change('city', e.target.value)}
            />
            <label htmlFor="cityInput">City</label>
            {validationErrors.city && (
              <div className="invalid-feedback">{validationErrors.city}</div>
            )}
          </div>
        </div>
      </div>

      {/* Postal Code - Full width */}
      <div className="form-floating mb-3">
        <input
          className={`form-control ${validationErrors.postcode ? 'is-invalid' : ''}`}
          type="text"
          id="postcodeInput"
          placeholder="Postal code/Zip"
          value={step2Data.postcode || ''}
          onChange={(e) => handleStep2Change('postcode', e.target.value)}
        />
        <label htmlFor="postcodeInput">Postal code/Zip</label>
        {validationErrors.postcode && (
          <div className="invalid-feedback">{validationErrors.postcode}</div>
        )}
      </div>

      <h3>Required Document</h3>
      <div id="Required Documents" className="row g-3 mb-3">
        {checkingAddressStatus ? (
          <div className="text-center text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
            Checking address verification status...
          </div>
        ) : isAddressVerified ? (
          <div className="alert alert-success d-flex align-items-center" role="alert">
            <svg className="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Success:">
              <use xlinkHref="#check-circle-fill"/>
            </svg>
            <div>
              <strong>✓ Address Verified</strong>
              <p className="mb-0">Your proof of address has already been verified. You can proceed to the next step.</p>
            </div>
          </div>
        ) : (
          <DragDropUpload
            documentType="proof_of_address"
            title="Proof of Address"
            onDocumentUploaded={handleDocumentUploaded}
            onDocumentUploadFailed={handleDocumentUploadFailed}
            uploadedFilenames={uploadedFilenames}
            isUploading={uploadingDocuments.has('proof_of_address')}
            onUploadStart={handleUploadStart}
            hasError={documentErrors.has('proof_of_address')}
            uploadEndpoint="/accounts/tutor-docs/"
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={previousStep}
        >
          Previous
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            'Next'
          )}
        </button>
      </div>

      <style>{`
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }
      `}</style>
    </div>
  );
};

// Step 3: Qualification
const Step3AcademicRecord = ({
  step3Data,
  handleStep3Change,
  validationErrors,
  setValidationErrors,
  previousStep,
  nextStep,
  loading,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleUploadStart
}) => {
  // State for qualifications grouped by level
  const [qualifications, setQualifications] = React.useState({
    bachelors: [],
    honors: [],
    masters: [],
    phd: []
  });

  const [savingQualification, setSavingQualification] = React.useState(false);
  const [deletingQualification, setDeletingQualification] = React.useState(null);
  const [rejectedQualifications, setRejectedQualifications] = React.useState([]);
  const [verifiedQualifications, setVerifiedQualifications] = React.useState([]);
  const [checkingRejectedStatus, setCheckingRejectedStatus] = React.useState(true);

  // Get user ID from localStorage
  const userId = localStorage.getItem('user_id');

  // Load rejected qualifications on component mount
  React.useEffect(() => {
    const fetchRejectedQualifications = async () => {
      try {
        if (!userId) {
          setCheckingRejectedStatus(false);
          return;
        }

        // Fetch all documents for the user
        const docsResponse = await axios.get(`/accounts/tutor-docs/?user=${userId}`);
        const documents = docsResponse.data;
        
        // Find rejected documents that have comments and are linked to qualifications
        const rejectedQualificationDocs = documents.filter(doc => 
          doc.doc_status === 'Rejected' && 
          doc.comments && 
          doc.comments.trim() !== '' &&
          doc.qualification // Must be linked to a qualification
        );
        
        // Find verified documents that are linked to qualifications
        const verifiedQualificationDocs = documents.filter(doc => 
          doc.doc_status === 'Verified' && 
          doc.qualification // Must be linked to a qualification
        );
        
        if (rejectedQualificationDocs.length === 0) {
          setRejectedQualifications([]);
        } else {
          // Group rejected documents by qualification ID, but exclude qualifications that have verified documents
          const qualificationGroups = {};
          rejectedQualificationDocs.forEach(doc => {
            const qualId = doc.qualification;
            
            // Check if this qualification already has a verified document
            const hasVerifiedDoc = documents.some(verifiedDoc => 
              verifiedDoc.qualification === qualId && 
              verifiedDoc.doc_status === 'Verified'
            );
            
            // Only include rejected docs if there's no verified document for this qualification
            if (!hasVerifiedDoc) {
              if (!qualificationGroups[qualId]) {
                qualificationGroups[qualId] = [];
              }
              qualificationGroups[qualId].push(doc);
            }
          });
          
          // If no qualifications have rejected docs (after filtering out verified ones), return empty
          if (Object.keys(qualificationGroups).length === 0) {
            setRejectedQualifications([]);
          } else {
            // Fetch qualification details for each group
            const qualificationsWithComments = await Promise.all(
              Object.entries(qualificationGroups).map(async ([qualId, docs]) => {
                try {
                  const qualResponse = await axios.get(`/accounts/tutor-qualifications/${qualId}/`);
                  const qualification = qualResponse.data;
                  
                  return {
                    ...qualification,
                    comments: docs.map(doc => doc.comments)
                  };
                } catch (error) {
                  console.error('Error fetching qualification details for ID:', qualId, error);
                  return null;
                }
              })
            );
            
            // Filter out null values and set the results
            setRejectedQualifications(qualificationsWithComments.filter(qual => qual !== null));
          }
        }
        
        // Handle verified qualifications
        if (verifiedQualificationDocs.length === 0) {
          setVerifiedQualifications([]);
        } else {
          // Group verified documents by qualification ID
          const verifiedQualificationIds = [...new Set(verifiedQualificationDocs.map(doc => doc.qualification))];
          
          // Fetch qualification details for each verified qualification
          const verifiedQualificationsWithDetails = await Promise.all(
            verifiedQualificationIds.map(async (qualId) => {
              try {
                const qualResponse = await axios.get(`/accounts/tutor-qualifications/${qualId}/`);
                const qualification = qualResponse.data;
                
                return qualification;
              } catch (error) {
                console.error('Error fetching verified qualification details for ID:', qualId, error);
                return null;
              }
            })
          );
          
          // Filter out null values and set the results
          setVerifiedQualifications(verifiedQualificationsWithDetails.filter(qual => qual !== null));
        }
      } catch (error) {
        console.error('Error fetching qualification statuses:', error);
      } finally {
        setCheckingRejectedStatus(false);
      }
    };

    fetchRejectedQualifications();
  }, [userId]);

  // Qualification level config
  const levelConfig = {
    bachelors: { 
      numeric: 1,
      value: 'Bachelors', // Backend value
      label: 'Bachelor\'s', // Display label
      prefix: 'Bachelor of',
      placeholder: 'Science in Physics'
    },
    honors: { 
      numeric: 2,
      value: 'Honors', // Backend value
      label: 'Honours', // Display label
      prefix: 'Honours of',
      placeholder: 'Science in Physics'
    },
    masters: { 
      numeric: 3,
      value: 'Masters', // Backend value
      label: 'Master\'s', // Display label
      prefix: 'Master\'s of',
      placeholder: 'Science in Physics'
    },
    phd: { 
      numeric: 4,
      value: 'PhD', // Backend value
      label: 'Doctorate', // Display label
      prefix: 'Doctorate of',
      placeholder: 'Philosophy in Physics'
    }
  };

  // Add new qualification to a level
  const addQualification = (level) => {
    const newQual = {
      tempId: `temp_${Date.now()}_${Math.random()}`, // Temporary ID until saved
      id: null, // Backend ID
      qualification_level: levelConfig[level].value, // Use backend value
      qualification_level_numeric: levelConfig[level].numeric,
      qualification_name: '',
      institution: '',
      year_obtained: '',
      documents: [], // Array of document IDs
      uploadedFilenames: [] // For UI display
    };

    setQualifications(prev => ({
      ...prev,
      [level]: [...prev[level], newQual]
    }));
  };

  // Update qualification field
  const updateQualification = (level, tempId, field, value) => {
    setQualifications(prev => ({
      ...prev,
      [level]: prev[level].map(qual => 
        qual.tempId === tempId 
          ? { ...qual, [field]: value }
          : qual
      )
    }));
  };

  // Save qualification to backend (creates the qualification record)
  const saveQualificationToBackend = async (level, tempId) => {
    console.log('saveQualificationToBackend called for:', level, tempId);
    const qual = qualifications[level].find(q => q.tempId === tempId);
    
    if (!qual) {
      console.error('Qualification not found:', level, tempId);
      return null;
    }
    
    if (qual.id) {
      console.log('Qualification already saved with ID:', qual.id);
      return qual.id; // Already saved
    }

    console.log('Qualification data:', qual);

    // Validate required fields
    if (!qual.qualification_name.trim()) {
      alert('Please enter a qualification name before uploading documents');
      console.error('Qualification name is empty');
      return null;
    }

    console.log('Saving qualification to backend...');
    setSavingQualification(true);
    try {
      const payload = {
        user: userId,
        qualification_level: qual.qualification_level,
        qualification_level_numeric: qual.qualification_level_numeric,
        qualification_name: qual.qualification_name,
        institution: qual.institution || '',
        year_obtained: qual.year_obtained ? parseInt(qual.year_obtained) : null
      };
      console.log('Payload:', payload);

      const response = await axios.post('/accounts/tutor-qualifications/', payload);

      console.log('Save qualification response status:', response.status);

      const savedQual = response.data;
      console.log('Qualification saved successfully:', savedQual);
      
      // Update state with backend ID
      setQualifications(prev => ({
        ...prev,
        [level]: prev[level].map(q => 
          q.tempId === tempId 
            ? { ...q, id: savedQual.id }
            : q
        )
      }));

      return savedQual.id;
    } catch (error) {
      console.error('Failed to save qualification:', error);
      alert('Failed to save qualification. Please try again.');
      return null;
    } finally {
      setSavingQualification(false);
    }
  };

  // Delete qualification
  const deleteQualification = async (level, tempId) => {
    const qual = qualifications[level].find(q => q.tempId === tempId);
    if (!qual) return;

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this qualification? All uploaded documents will also be removed.')) {
      return;
    }

    setDeletingQualification(tempId);

    try {
      // If saved to backend, delete from backend
      if (qual.id) {
        await axios.delete(`/accounts/tutor-qualifications/${qual.id}/`);
      }

      // Remove from local state
      setQualifications(prev => ({
        ...prev,
        [level]: prev[level].filter(q => q.tempId !== tempId)
      }));
    } catch (error) {
      console.error('Failed to delete qualification:', error);
      alert('Failed to delete qualification. Please try again.');
    } finally {
      setDeletingQualification(null);
    }
  };

  // Handle document upload for a specific qualification
  const handleQualificationDocumentUpload = async (level, tempId, file) => {
    console.log('Starting document upload for qualification:', level, tempId, file.name);
    
    // Get the qualification details
    const qual = qualifications[level].find(q => q.tempId === tempId);
    if (!qual) {
      console.error('Qualification not found:', level, tempId);
      return;
    }
    
    // First, ensure the qualification is saved to get an ID
    const qualificationId = await saveQualificationToBackend(level, tempId);
    if (!qualificationId) {
      console.error('Failed to get qualification ID');
      return;
    }
    console.log('Qualification saved/loaded with ID:', qualificationId);

    // Now upload the document with the qualification ID
    const formData = new FormData();
    formData.append('document', file);
    formData.append('user', userId);
    formData.append('qualification', qualificationId);
    formData.append('doc_type', qual.qualification_name || 'certificate'); // Use qualification name as doc type

    try {
      handleUploadStart(`${level}_${tempId}`); // Mark as uploading
      console.log('Upload started, sending to backend...');

      const response = await axios.post('/accounts/tutor-docs/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Response status:', response.status, response.statusText);

      const uploadedDoc = response.data;
      console.log('Document uploaded successfully:', uploadedDoc);

      // Update qualification's document list
      setQualifications(prev => ({
        ...prev,
        [level]: prev[level].map(qual => 
          qual.tempId === tempId 
            ? { 
                ...qual, 
                documents: [...qual.documents, uploadedDoc.id],
                uploadedFilenames: [...qual.uploadedFilenames, file.name]
              }
            : qual
        )
      }));

      console.log('State updated, calling handleDocumentUploaded');
      // Call the document uploaded handler to update parent state
      handleDocumentUploaded(`${level}_${tempId}`, file.name);
      
      // Clear any validation errors for this qualification
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${level}_${tempId}_doc`];
        return newErrors;
      });
      
      console.log('Upload complete and state updated');
    } catch (error) {
      console.error('Document upload failed:', error);
      handleDocumentUploadFailed(`${level}_${tempId}`, error.message);
    }
  };

  // Validation on submit
  const handleSubmit = () => {
    const errors = {};

    // Validate each qualification has at least one document
    Object.entries(qualifications).forEach(([level, quals]) => {
      quals.forEach((qual, index) => {
        if (qual.documents.length === 0) {
          errors[`${level}_${qual.tempId}_doc`] = `Please upload at least one document for this ${levelConfig[level].label} qualification`;
        }
      });
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      if (firstError !== 'general') {
        const errorElement = document.getElementById(firstError);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    setValidationErrors({});
    nextStep();
  };

  return (
    <div>
      <h3>Academic Qualifications</h3>
      
      {/* Verified Qualifications Success */}
      {!checkingRejectedStatus && verifiedQualifications.length > 0 && (
        <div className="mb-4">
          <div className="alert alert-success d-flex align-items-start mb-3" role="alert">
            <svg className="bi flex-shrink-0 me-2 mt-1" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm3.5-9.5a.5.5 0 0 0-.708-.708L8.146 8.439 6.354 6.646a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l3-3z"/>
            </svg>
            <div>
              <strong>✓ Verified Qualifications:</strong>
              <div className="mt-1">
                {verifiedQualifications.map((qual, index) => (
                  <div key={qual.id || index} className="mb-1">
                    {qual.qualification_name} ({qual.qualification_level})
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Rejected Qualifications Warning */}
      {!checkingRejectedStatus && rejectedQualifications.length > 0 && (
        <div className="mb-4">
          {rejectedQualifications.map((qual, index) => (
            <div key={qual.id || index} className="alert alert-warning d-flex align-items-start mb-3" role="alert">
              <svg className="bi flex-shrink-0 me-2 mt-1" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
              </svg>
              <div>
                <strong>Qualification Issue: {qual.qualification_name} ({qual.qualification_level})</strong>
                {qual.comments.map((comment, commentIndex) => (
                  <p key={commentIndex} className="mb-1">{comment}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {validationErrors.general && (
        <div className="alert alert-danger" role="alert">
          {validationErrors.general}
        </div>
      )}

      {/* Render each qualification level */}
      {Object.entries(levelConfig).map(([levelKey, config]) => (
        <div key={levelKey} className="qualification-level-section mb-5">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="qualification-level-title">{config.label}</h4>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => addQualification(levelKey)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add {config.label}
            </button>
          </div>

          {qualifications[levelKey].length === 0 && (
            <div className="text-muted fst-italic mb-3">
              No {config.label} qualifications added yet
            </div>
          )}

          {/* Render each qualification card */}
          {qualifications[levelKey].map((qual, index) => (
            <div 
              key={qual.tempId} 
              className="qualification-card mb-3 p-3 border rounded bg-light"
              id={`${levelKey}_${qual.tempId}_card`}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <span className="badge bg-secondary">
                  {config.label} #{index + 1}
                  {qual.id && <span className="ms-1" title="Saved to database">✓</span>}
                </span>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => deleteQualification(levelKey, qual.tempId)}
                  disabled={deletingQualification === qual.tempId}
                >
                  {deletingQualification === qual.tempId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <i className="bi bi-trash"></i>
                  )}
                </button>
              </div>

              {/* Qualification Name with Prefix */}
              <div className="mb-3">
                <label className="form-label small">Qualification Name *</label>
                <div className="d-flex align-items-center border rounded px-3 py-2 bg-white">
                  <span className="text-secondary me-2 fw-medium" style={{ whiteSpace: 'nowrap' }}>
                    {config.prefix}
                  </span>
                  <input
                    type="text"
                    className="border-0 flex-grow-1"
                    style={{ outline: 'none' }}
                    placeholder={config.placeholder}
                    value={qual.qualification_name}
                    onChange={(e) => updateQualification(levelKey, qual.tempId, 'qualification_name', e.target.value)}
                  />
                </div>
                <div className="text-muted small mt-1">
                  Full name: <strong>{config.prefix} {qual.qualification_name || '...'}</strong>
                </div>
              </div>

              {/* Institution */}
              <div className="mb-3">
                <label className="form-label small">Institution (Optional)</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="e.g., University of Cape Town"
                  value={qual.institution}
                  onChange={(e) => updateQualification(levelKey, qual.tempId, 'institution', e.target.value)}
                />
              </div>

              {/* Year Obtained */}
              <div className="mb-3">
                <label className="form-label small">Year Obtained (Optional)</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="e.g., 2020"
                  min="1950"
                  max={new Date().getFullYear()}
                  value={qual.year_obtained}
                  onChange={(e) => updateQualification(levelKey, qual.tempId, 'year_obtained', e.target.value)}
                />
              </div>

              {/* Document Upload */}
              <div className="mb-2" id={`${levelKey}_${qual.tempId}_doc`}>
                <label className="form-label small">Upload Documents *</label>
                <DragDropUpload
                  documentType={`${levelKey}_${qual.tempId}`}
                  title={`${config.label} Certificate/Transcript`}
                  onDocumentUploaded={(docType, docId, filename) => {
                    // This is called after successful upload
                    // We handle the state update in handleQualificationDocumentUpload
                  }}
                  onDocumentUploadFailed={handleDocumentUploadFailed}
                  uploadedFilenames={qual.uploadedFilenames.length > 0 ? new Map([[`${levelKey}_${qual.tempId}`, qual.uploadedFilenames]]) : new Map()}
                  isUploading={uploadingDocuments?.has(`${levelKey}_${qual.tempId}`)}
                  onUploadStart={handleUploadStart}
                  hasError={!!validationErrors[`${levelKey}_${qual.tempId}_doc`]}
                  uploadEndpoint="/accounts/tutor-docs/"
                  compact={true}
                  customUploadHandler={(file) => handleQualificationDocumentUpload(levelKey, qual.tempId, file)}
                />
                {validationErrors[`${levelKey}_${qual.tempId}_doc`] && (
                  <div className="text-danger small mt-1">
                    {validationErrors[`${levelKey}_${qual.tempId}_doc`]}
                  </div>
                )}
                {qual.documents.length > 0 && (
                  <div className="text-success small mt-1">
                    <i className="bi bi-check-circle me-1"></i>
                    {qual.documents.length} document{qual.documents.length > 1 ? 's' : ''} uploaded
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Navigation buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={previousStep}
          disabled={loading || savingQualification || deletingQualification}
        >
          Previous
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading || savingQualification || deletingQualification}
        >
          {loading || savingQualification ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {savingQualification ? 'Saving...' : 'Processing...'}
            </>
          ) : (
            'Next'
          )}
        </button>
      </div>

      <style>{`
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }

        .qualification-level-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #212529;
          margin: 0;
        }

        .qualification-card {
          background-color: #f8f9fa !important;
          transition: box-shadow 0.2s;
        }

        .qualification-card:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .form-label.small {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.25rem;
        }
      `}</style>
    </div>
  );
};

// Step 4: Subjects
const Step4Subjects = ({
  step4Data,
  handleStep4Change,
  validationErrors,
  setValidationErrors,
  previousStep,
  nextStep,
  loading
}) => {
  const [allSubjects, setAllSubjects] = React.useState([]);
  const [loadingSubjects, setLoadingSubjects] = React.useState(true);
  const [selectedSubjects, setSelectedSubjects] = React.useState([]);
  const [branchTeachingLevels, setBranchTeachingLevels] = React.useState({});

  // Teaching level options
  const teachingLevels = [
    { id: 'undergraduate', label: 'Undergraduate', color: 'info' },
    { id: 'honors', label: 'Honours', color: 'primary' },
    { id: 'masters', label: 'Masters', color: 'success' },
    { id: 'doctorate', label: 'Doctorate', color: 'warning' }
  ];

  React.useEffect(() => {
    console.log('Step4Subjects component mounted/updated');
    console.log('step4Data.branchTeachingLevels:', step4Data.branchTeachingLevels);
    
    // Initialize branchTeachingLevels from step4Data if available
    if (step4Data.branchTeachingLevels) {
      console.log('Restoring teaching levels from step4Data:', step4Data.branchTeachingLevels);
      setBranchTeachingLevels(step4Data.branchTeachingLevels);
    }
    
    // Fetch subjects with their branches from API
    const fetchSubjects = async () => {
      try {
        const response = await axios.get('/api/subjects/');
        console.log('Subjects loaded:', response.data);
        setAllSubjects(response.data);
      } catch (error) {
        console.error('Error fetching subjects:', error);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [step4Data.branchTeachingLevels]);

  // Pre-populate existing branches on component mount
  React.useEffect(() => {
    const loadExistingBranches = async () => {
      // If we already have branchTeachingLevels in step4Data, restore from that instead
      if (step4Data.branchTeachingLevels && Object.keys(step4Data.branchTeachingLevels).length > 0) {
        console.log('Restoring from step4Data:', step4Data.branchTeachingLevels);
        setBranchTeachingLevels(step4Data.branchTeachingLevels);
        
        // Set up selected subjects based on existing branches
        if (step4Data.branches && step4Data.branches.length > 0 && allSubjects.length > 0) {
          const subjectIds = new Set();
          allSubjects.forEach(subject => {
            if (subject.branches.some(branch => step4Data.branches.includes(branch.id))) {
              subjectIds.add(subject.id);
            }
          });
          setSelectedSubjects([...subjectIds]);
        }
        return; // Don't load from API if we have step data
      }
      
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) return;

        // Try to load existing tutor branches with teaching levels
        try {
          const branchesResponse = await axios.get(`/accounts/tutors/${userId}/branches/`);
          const existingBranches = branchesResponse.data;
          
          if (existingBranches.length > 0) {
            // Set up branch teaching levels
            const levelsMap = {};
            const branchIds = [];
            const subjectIds = new Set();
            
            existingBranches.forEach(tb => {
              levelsMap[tb.branch_id] = tb.teaching_levels || [];
              branchIds.push(tb.branch_id);
              subjectIds.add(tb.branch_details.subject_id);
            });
            
            console.log('Loading from API - teaching levels:', levelsMap);
            setBranchTeachingLevels(levelsMap);
            handleStep4Change('branchTeachingLevels', levelsMap);
            handleStep4Change('branches', branchIds);
            setSelectedSubjects([...subjectIds]);
          }
        } catch (branchError) {
          // If branches endpoint doesn't exist or fails, try old method
          console.log('Using fallback method for existing branches');
          const response = await axios.get(`/accounts/tutors/${userId}/profile/get/`);
          const tutorData = response.data;
          
          if (tutorData.branches && tutorData.branches.length > 0) {
            handleStep4Change('branches', tutorData.branches);
            handleStep4Change('subject_category', tutorData.subject_category || 'STEM');
          }
        }
      } catch (error) {
        console.error('Error fetching existing branches:', error);
      }
    };

    loadExistingBranches();
  }, [allSubjects.length]); // Only run when subjects are loaded, not on every render

  // Filter subjects based on user's category
  const filteredSubjects = allSubjects.filter(
    subject => subject.category === step4Data.subject_category
  );

  const handleSubjectSelect = (e) => {
    const subjectId = parseInt(e.target.value);
    if (subjectId && !selectedSubjects.includes(subjectId)) {
      setSelectedSubjects([...selectedSubjects, subjectId]);
    }
  };

  const handleSubjectRemove = (subjectId) => {
    setSelectedSubjects(selectedSubjects.filter(id => id !== subjectId));
    
    // Also remove all branches from this subject
    const subject = allSubjects.find(s => s.id === subjectId);
    if (subject) {
      const branchIdsToRemove = subject.branches.map(b => b.id);
      const updatedBranches = (step4Data.branches || []).filter(
        branchId => !branchIdsToRemove.includes(branchId)
      );
      handleStep4Change('branches', updatedBranches);
      
      // Remove teaching levels for these branches
      const updatedLevels = { ...branchTeachingLevels };
      branchIdsToRemove.forEach(branchId => {
        delete updatedLevels[branchId];
      });
      setBranchTeachingLevels(updatedLevels);
      handleStep4Change('branchTeachingLevels', updatedLevels);
    }
  };

  const handleBranchToggle = (branchId) => {
    const currentBranches = step4Data.branches || [];
    if (currentBranches.includes(branchId)) {
      // Remove branch
      handleStep4Change('branches', currentBranches.filter(id => id !== branchId));
      // Remove teaching levels for this branch
      const updatedLevels = { ...branchTeachingLevels };
      delete updatedLevels[branchId];
      setBranchTeachingLevels(updatedLevels);
      handleStep4Change('branchTeachingLevels', updatedLevels);
    } else {
      // Add branch
      handleStep4Change('branches', [...currentBranches, branchId]);
      // Initialize empty teaching levels for this branch
      const updatedLevels = {
        ...branchTeachingLevels,
        [branchId]: []
      };
      setBranchTeachingLevels(updatedLevels);
      handleStep4Change('branchTeachingLevels', updatedLevels);
    }
  };

  const handleTeachingLevelToggle = (branchId, level) => {
    console.log(`Toggling ${level} for branch ${branchId}`);
    const currentLevels = branchTeachingLevels[branchId] || [];
    const hasLevel = currentLevels.includes(level);
    
    const newLevels = hasLevel 
      ? currentLevels.filter(l => l !== level)
      : [...currentLevels, level];
    
    const updatedBranchLevels = {
      ...branchTeachingLevels,
      [branchId]: newLevels
    };
    
    console.log(`Branch ${branchId} levels updated:`, newLevels);
    
    setBranchTeachingLevels(updatedBranchLevels);
    
    // Always keep step4Data updated so it persists when navigating between steps
    handleStep4Change('branchTeachingLevels', updatedBranchLevels);
  };

  const handleNext = () => {
    const errors = {};
    
    if (!step4Data.branches || step4Data.branches.length === 0) {
      errors.branches = 'Please select at least one subject branch';
    } else {
      // Check that selected branches have teaching levels
      const branchesWithoutLevels = step4Data.branches.filter(branchId => {
        const levels = branchTeachingLevels[branchId] || [];
        return levels.length === 0;
      });
      
      if (branchesWithoutLevels.length > 0) {
        errors.teachingLevels = 'Please select teaching levels for all selected branches';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Ensure the teaching levels data is saved to step4Data
    handleStep4Change('branchTeachingLevels', branchTeachingLevels);
    
    setValidationErrors({});
    nextStep();
  };

  if (loadingSubjects) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading subjects...</span>
        </div>
        <p className="mt-3 text-muted">Loading subjects...</p>
      </div>
    );
  }

  const categoryDisplay = step4Data.subject_category === 'STEM' ? 'STEM' : 'Arts';

  return (
    <div>
      <h3>Subjects</h3>
      <div className="alert alert-info mb-4">
        <strong>Your Category:</strong> {categoryDisplay}
        <p className="mb-0 mt-1 small">You can select subjects from the {categoryDisplay} category below.</p>
      </div>

      {validationErrors.branches && (
        <div className="alert alert-danger">{validationErrors.branches}</div>
      )}
      
      {validationErrors.teachingLevels && (
        <div className="alert alert-warning">{validationErrors.teachingLevels}</div>
      )}

      {/* Subject Selection Dropdown */}
      <div className="mb-4">
        <label htmlFor="subjectSelect" className="form-label fw-bold">Select a Subject</label>
        <select 
          id="subjectSelect"
          className="form-select"
          onChange={handleSubjectSelect}
          value=""
        >
          <option value="">-- Choose a subject to add --</option>
          {filteredSubjects.map((subject) => {
            const isSelected = selectedSubjects.includes(subject.id);
            return (
              <option 
                key={subject.id} 
                value={subject.id}
                disabled={isSelected}
              >
                {subject.name}{isSelected ? ' (Added)' : ''}
              </option>
            );
          })}
        </select>
      </div>

      {/* Display selected subjects with their branches */}
      {selectedSubjects.length > 0 ? (
        <div>
          <h5 className="mb-3">Selected Subjects & Branches</h5>
          {selectedSubjects.map((subjectId) => {
            const subject = allSubjects.find(s => s.id === subjectId);
            if (!subject) return null;

            return (
              <div key={subject.id} className="card mb-3">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                  <span className="fw-bold">{subject.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => handleSubjectRemove(subject.id)}
                    title="Remove this subject"
                  >
                    ✕ Remove
                  </button>
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-3">Select the branches you can teach and specify your teaching levels:</p>
                  <div className="row g-3">
                    {subject.branches.map((branch) => {
                      const isBranchSelected = step4Data.branches?.includes(branch.id) || false;
                      const branchLevels = branchTeachingLevels[branch.id] || [];
                      
                      return (
                        <div key={branch.id} className="col-12">
                          <div 
                            className={`border rounded p-3 ${
                              isBranchSelected 
                                ? 'bg-primary bg-opacity-10 border-primary' 
                                : 'bg-white'
                            }`}
                          >
                            {/* Branch Selection */}
                            <div className="form-check mb-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`branch-${branch.id}`}
                                checked={isBranchSelected}
                                onChange={() => handleBranchToggle(branch.id)}
                              />
                              <label 
                                className="form-check-label fw-bold" 
                                htmlFor={`branch-${branch.id}`}
                              >
                                {branch.name}
                                {isBranchSelected && branchLevels.length > 0 && (
                                  <span className="badge bg-success ms-2">{branchLevels.length} level(s)</span>
                                )}
                                {isBranchSelected && branchLevels.length === 0 && (
                                  <span className="badge bg-warning ms-2">No levels selected</span>
                                )}
                              </label>
                            </div>
                            
                            {/* Teaching Levels (only show if branch is selected) */}
                            {isBranchSelected && (
                              <div className="ms-3">
                                <p className="small text-muted mb-2">Select which levels you can teach for this branch:</p>
                                <div className="row g-2">
                                  {teachingLevels.map(level => {
                                    const isLevelSelected = branchLevels.includes(level.id);
                                    return (
                                      <div key={level.id} className="col-6 col-md-3">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`level-${branch.id}-${level.id}`}
                                            checked={isLevelSelected}
                                            onChange={() => handleTeachingLevelToggle(branch.id, level.id)}
                                          />
                                          <label 
                                            className="form-check-label small" 
                                            htmlFor={`level-${branch.id}-${level.id}`}
                                          >
                                            <span className={`badge bg-${level.color} ${isLevelSelected ? '' : 'bg-opacity-50'}`}>
                                              {level.label}
                                            </span>
                                          </label>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                
                                {branchLevels.length === 0 && (
                                  <div className="alert alert-warning mt-2 py-2">
                                    <small>
                                      <i className="bi bi-exclamation-triangle me-1"></i>
                                      Please select at least one teaching level for this branch
                                    </small>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="alert alert-secondary">
          <h6 className="mb-2">No subjects selected yet</h6>
          <p className="mb-0">
            Please select a subject from the dropdown above to start choosing branches and teaching levels.
          </p>
        </div>
      )}

      {/* Summary */}
      {selectedSubjects.length > 0 && (
        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="mb-2">Summary:</h6>
          <div className="small text-muted">
            <div>• {selectedSubjects.length} subject(s) selected</div>
            <div>• {(step4Data.branches || []).length} branch(es) selected</div>
            <div>• {Object.keys(branchTeachingLevels).reduce((total, branchId) => total + (branchTeachingLevels[branchId] || []).length, 0)} teaching level(s) configured</div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={previousStep}
        >
          Previous
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleNext}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            'Next'
          )}
        </button>
      </div>

      <style>{`
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }

        h5 {
          font-size: 18px;
          font-weight: 600;
          color: #212529;
        }
      `}</style>
    </div>
  );
};

// Step 5: Banking
const Step5Banking = ({
  step5Data,
  handleStep5Change,
  validationErrors,
  setValidationErrors,
  previousStep,
  onSubmit,
  loading,
  // Document upload props
  uploadedDocuments,
  uploadingDocuments,
  uploadedFilenames,
  documentErrors,
  setDocumentErrors,
  handleDocumentUploaded,
  handleDocumentUploadFailed,
  handleUploadStart
}) => {
  const [isBankingVerified, setIsBankingVerified] = useState(false);
  const [checkingBankingStatus, setCheckingBankingStatus] = useState(true);
  const [rejectionComments, setRejectionComments] = useState('');
  const [verifiedBankingDetails, setVerifiedBankingDetails] = useState(null);
  const [loadingBankingDetails, setLoadingBankingDetails] = useState(false);

  // Check if user already has verified proof of bank account document
  useEffect(() => {
    const checkBankingVerification = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) {
          setCheckingBankingStatus(false);
          return;
        }

        const response = await axios.get(`/accounts/tutor-docs/?user=${userId}`);
        const documents = response.data;
        
        // Check if there's a verified Proof of Bank Account document
        const hasVerifiedBanking = documents.some(doc => 
          doc.doc_type === 'Proof of Bank Account' && doc.doc_status === 'Verified'
        );
        
        // Only check for rejected documents if there's NO verified document
        let rejectedDoc = null;
        if (!hasVerifiedBanking) {
          rejectedDoc = documents.find(doc => 
            doc.doc_type === 'Proof of Bank Account' && 
            doc.doc_status === 'Rejected' && 
            doc.comments && 
            doc.comments.trim() !== ''
          );
        }
        
        setIsBankingVerified(hasVerifiedBanking);
        setRejectionComments(rejectedDoc ? rejectedDoc.comments : '');
        
        // If banking is verified, fetch the banking details
        if (hasVerifiedBanking) {
          fetchVerifiedBankingDetails(userId);
        }
      } catch (error) {
        console.error('Error checking banking verification status:', error);
      } finally {
        setCheckingBankingStatus(false);
      }
    };

    checkBankingVerification();
  }, []);

  // Fetch verified banking details
  const fetchVerifiedBankingDetails = async (userId) => {
    setLoadingBankingDetails(true);
    try {
      const response = await axios.get(`/accounts/tutors/${userId}/`);
      const tutorData = response.data;
      
      if (tutorData.bank_name || tutorData.account_holder_name || tutorData.account_number || 
          tutorData.account_type || tutorData.branch_code) {
        const bankingDetails = {
          bank_name: tutorData.bank_name,
          account_holder_name: tutorData.account_holder_name,
          account_number: tutorData.account_number,
          account_type: tutorData.account_type,
          branch_code: tutorData.branch_code
        };
        
        setVerifiedBankingDetails(bankingDetails);
        
        // Also populate step5Data with verified details so they're available for final submission
        Object.keys(bankingDetails).forEach(key => {
          if (bankingDetails[key]) {
            handleStep5Change(key, bankingDetails[key]);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching verified banking details:', error);
    } finally {
      setLoadingBankingDetails(false);
    }
  };
  const handleSubmit = () => {
    console.log('Banking step submit clicked');
    console.log('isBankingVerified:', isBankingVerified);
    console.log('step5Data:', step5Data);
    console.log('uploadedDocuments:', uploadedDocuments);
    
    const errors = {};
    
    // Only validate banking details if not already verified
    if (!isBankingVerified) {
      console.log('Banking not verified, validating fields...');
      if (!step5Data.bank_name) errors.bank_name = 'Bank name is required';
      if (!step5Data.account_holder_name) errors.account_holder_name = 'Account holder name is required';
      if (!step5Data.account_number) errors.account_number = 'Account number is required';
      if (!step5Data.account_type) errors.account_type = 'Account type is required';
      if (!step5Data.branch_code) errors.branch_code = 'Branch code is required';
      
      // Validate proof of account document only if not already verified
      if (!uploadedDocuments.has('proof_of_account')) {
        errors.proof_of_account = 'Proof of account document is required';
        setDocumentErrors(new Set(['proof_of_account']));
      }
    } else {
      console.log('Banking already verified, skipping field validation');
    }
    
    console.log('Validation errors:', errors);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (errors.proof_of_account) {
        document.getElementById('proofOfAccountSection')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
      return;
    }
    
    console.log('Banking step validation passed, calling onSubmit');
    setValidationErrors({});
    onSubmit();
  };

  return (
    <div>
      <h3>Banking Details</h3>
      
      {/* Rejection Comments Warning */}
      {rejectionComments && (
        <div className="alert alert-warning d-flex align-items-start mb-3" role="alert">
          <svg className="bi flex-shrink-0 me-2 mt-1" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
          </svg>
          <div>
            <strong>Proof of Bank Account Issue</strong>
            <p className="mb-0">{rejectionComments}</p>
          </div>
        </div>
      )}
      
      <p className="text-muted mb-4">Provide your banking information for payments</p>

      {/* Show verified banking details if banking is verified */}
      {isBankingVerified ? (
        <div className="verified-banking-details mb-4">
          <div className="alert alert-success d-flex align-items-start mb-3" role="alert">
            <svg className="bi flex-shrink-0 me-2" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm3.5-9.5a.5.5 0 0 0-.708-.708L8.146 8.439 6.354 6.646a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l3-3z"/>
            </svg>
            <div>
              <strong>✓ Banking Details Verified</strong>
              <p className="mb-0">Your banking information has been verified.</p>
            </div>
          </div>
          
          {loadingBankingDetails ? (
            <div className="text-center text-muted mb-4">
              <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
              Loading verified banking details...
            </div>
          ) : verifiedBankingDetails ? (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={verifiedBankingDetails.bank_name || ''}
                    readOnly
                  />
                  <label>Bank Name</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={verifiedBankingDetails.account_holder_name || ''}
                    readOnly
                  />
                  <label>Account Holder Name</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={verifiedBankingDetails.account_number || ''}
                    readOnly
                  />
                  <label>Account Number</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={verifiedBankingDetails.account_type || ''}
                    readOnly
                  />
                  <label>Account Type</label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={verifiedBankingDetails.branch_code || ''}
                    readOnly
                  />
                  <label>Branch Code</label>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-info" role="alert">
              <strong>Banking documents verified</strong> - Banking details will be populated from your verified documents.
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.bank_name ? 'is-invalid' : ''}`}
              type="text"
              id="bankNameInput"
              placeholder="Bank Name"
              value={step5Data.bank_name}
              onChange={(e) => handleStep5Change('bank_name', e.target.value)}
            />
            <label htmlFor="bankNameInput">Bank Name</label>
            {validationErrors.bank_name && (
              <div className="invalid-feedback">{validationErrors.bank_name}</div>
            )}
          </div>

          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.account_holder_name ? 'is-invalid' : ''}`}
              type="text"
              id="accountHolderNameInput"
              placeholder="Account Holder Name"
              value={step5Data.account_holder_name}
              onChange={(e) => handleStep5Change('account_holder_name', e.target.value)}
            />
            <label htmlFor="accountHolderNameInput">Account Holder Name</label>
            {validationErrors.account_holder_name && (
              <div className="invalid-feedback">{validationErrors.account_holder_name}</div>
            )}
          </div>

          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.account_number ? 'is-invalid' : ''}`}
              type="text"
              id="accountNumberInput"
              placeholder="Account Number"
              value={step5Data.account_number}
              onChange={(e) => handleStep5Change('account_number', e.target.value)}
            />
            <label htmlFor="accountNumberInput">Account Number</label>
            {validationErrors.account_number && (
              <div className="invalid-feedback">{validationErrors.account_number}</div>
            )}
          </div>

          <div className="form-floating mb-3">
            <select
              className={`form-select ${validationErrors.account_type ? 'is-invalid' : ''}`}
              id="accountTypeSelect"
              value={step5Data.account_type}
              onChange={(e) => handleStep5Change('account_type', e.target.value)}
            >
              <option value="">Select Account Type</option>
              <option value="Savings">Savings</option>
              <option value="Cheque">Cheque</option>
              <option value="Current">Current</option>
            </select>
            <label htmlFor="accountTypeSelect">Account Type</label>
            {validationErrors.account_type && (
              <div className="invalid-feedback">{validationErrors.account_type}</div>
            )}
          </div>

          <div className="form-floating mb-3">
            <input
              className={`form-control ${validationErrors.branch_code ? 'is-invalid' : ''}`}
              type="text"
              id="branchCodeInput"
              placeholder="Branch Code"
              value={step5Data.branch_code}
              onChange={(e) => handleStep5Change('branch_code', e.target.value)}
            />
            <label htmlFor="branchCodeInput">Branch Code</label>
            {validationErrors.branch_code && (
              <div className="invalid-feedback">{validationErrors.branch_code}</div>
            )}
          </div>
        </>
      )}

      <h3 className="mt-4">Proof of Account</h3>
      <p className="text-muted mb-3">Upload a bank statement or proof of account document</p>
      <div id="proofOfAccountSection" className="row g-3 mb-4">
        {checkingBankingStatus ? (
          <div className="text-center text-muted">
            <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
            Checking banking verification status...
          </div>
        ) : isBankingVerified ? (
          <div className="alert alert-success d-flex align-items-center" role="alert">
            <svg className="bi flex-shrink-0 me-2" width="24" height="24" role="img" aria-label="Success:">
              <use xlinkHref="#check-circle-fill"/>
            </svg>
            <div>
              <strong>✓ Banking Verified</strong>
              <p className="mb-0">Your proof of bank account has already been verified. You can proceed to complete registration.</p>
            </div>
          </div>
        ) : (
          <>
            <DragDropUpload
              documentType="proof_of_account"
              title="Proof of Account Document"
              onDocumentUploaded={handleDocumentUploaded}
              onDocumentUploadFailed={handleDocumentUploadFailed}
              uploadedFilenames={uploadedFilenames}
              isUploading={uploadingDocuments.has('proof_of_account')}
              onUploadStart={handleUploadStart}
              hasError={documentErrors.has('proof_of_account')}
              uploadEndpoint="/accounts/tutor-docs/"
            />
            {validationErrors.proof_of_account && (
              <div className="alert alert-danger mt-2">{validationErrors.proof_of_account}</div>
            )}
          </>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="d-flex justify-content-between mt-4">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={previousStep}
        >
          Previous
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Completing Registration...
            </>
          ) : (
            'Complete Registration'
          )}
        </button>
      </div>

      <style>{`
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }
      `}</style>
    </div>
  );
};

// Completion Success Component
const CompletionSuccess = () => {
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '3rem 1rem',
      maxWidth: '600px',
      margin: '0 auto'
    }}>
      <div style={{ marginBottom: '2rem' }}>
        <i className="bi bi-file-earmark-check" style={{ fontSize: '4rem', color: '#0d6efd' }}></i>
      </div>
      <div style={{ 
        backgroundColor: '#cfe2ff',
        border: '1px solid #9ec5fe',
        borderRadius: '0.375rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 0.5rem 1rem rgba(13, 110, 253, 0.15)'
      }}>
        <h4 style={{ color: '#084298', marginBottom: '1rem', fontWeight: 'bold' }}>
          <i className="bi bi-hourglass-split" style={{ marginRight: '0.5rem' }}></i>
          Registration Submitted Successfully!
        </h4>
        <p style={{ color: '#084298', fontSize: '1.1rem', margin: '0' }}>
          Your tutor profile and documents have been submitted for review.
        </p>
      </div>
      <div style={{ color: '#6c757d' }}>
        <p style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>
          <strong>What happens next?</strong>
        </p>
        <p style={{ margin: '0.5rem 0' }}>
          Our team is currently reviewing your documents and profile information. You will be notified via email once the review is complete.
        </p>
        <p style={{ margin: '1rem 0 0.5rem 0', color: '#495057', fontWeight: '500' }}>
          Thank you for your patience!
        </p>
      </div>
    </div>
  );
};

// Main Tutor Form Wizard Component
const TutorFormWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const stepLabels = [
    { label: "Identification", icon: "bi-person-badge" },
    { label: "Address", icon: "bi-house-door" },
    { label: "Qualification", icon: "bi-journal-bookmark" },
    { label: "Subjects", icon: "bi-book" },
    { label: "Banking", icon: "bi-bank" }
  ];

  const [step1Data, setStep1Data] = useState({
    surname: '',
    other_names: '',
    citizenship: '',
    id_number: '',
    phone_number: '',
    gender: ''
  });

  const [step2Data, setStep2Data] = useState({
    country: 'South Africa',
    addresslines: '',
    addresslines2: '',
    suburb: '',
    city: '',
    postcode: '',
    province: ''
  });

  const [step3Data, setStep3Data] = useState({
    qualification: '',
    honours_qualification: '',
    masters_qualification: '',
    doctorate_qualification: ''
  });

  const [step4Data, setStep4Data] = useState({
    branches: [],
    subject_category: 'STEM'
  });

  const [step5Data, setStep5Data] = useState({
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    account_type: '',
    branch_code: ''
  });

  // Document upload state
  const [uploadedDocuments, setUploadedDocuments] = useState(new Set());
  const [uploadedFilenames, setUploadedFilenames] = useState(new Map());
  const [documentErrors, setDocumentErrors] = useState(new Set());
  const [uploadingDocuments, setUploadingDocuments] = useState(new Set());

  // Fetch existing tutor data on mount
  useEffect(() => {
    const fetchTutorData = async () => {
      try {
        const userId = localStorage.getItem("user_id");
        if (!userId) return;

        const response = await axios.get(`/accounts/tutors/${userId}/profile/get/`);
        const data = response.data;

        // Pre-fill step 1 data
        setStep1Data({
          surname: data.surname || '',
          other_names: data.other_names || '',
          citizenship: data.citizenship || '',
          id_number: data.id_number || '',
          phone_number: (data.phone_number && data.phone_number !== 'pending') ? data.phone_number : '',
          gender: data.gender || ''
        });

        // Pre-fill step 2 data
        setStep2Data({
          country: data.country || 'South Africa',
          addresslines: data.addresslines || '',
          addresslines2: '',
          suburb: data.suburb || '',
          city: data.city || '',
          postcode: data.postcode || '',
          province: data.province || ''
        });

        // Pre-fill step 3 data
        // Strip "Bachelor of " prefix if it exists when loading
        let qualification = data.qualification || '';
        if (qualification === 'N/A' || qualification === 'Fast Tracked') {
          qualification = '';
        } else if (qualification.startsWith('Bachelor of ')) {
          qualification = qualification.replace('Bachelor of ', '');
        }
        
        // Strip "Honours of " prefix if it exists when loading
        let honours_qualification = data.honours_qualification || '';
        if (honours_qualification === 'N/A' || honours_qualification === 'Fast Tracked') {
          honours_qualification = '';
        } else if (honours_qualification.startsWith('Honours of ')) {
          honours_qualification = honours_qualification.replace('Honours of ', '');
        }
        
        // Strip "Master's of " prefix if it exists when loading
        let masters_qualification = data.masters_qualification || '';
        if (masters_qualification === 'N/A' || masters_qualification === 'Fast Tracked') {
          masters_qualification = '';
        } else if (masters_qualification.startsWith("Master's of ")) {
          masters_qualification = masters_qualification.replace("Master's of ", '');
        }
        
        // Strip "Doctorate of " prefix if it exists when loading
        let doctorate_qualification = data.doctorate_qualification || '';
        if (doctorate_qualification === 'N/A' || doctorate_qualification === 'Fast Tracked') {
          doctorate_qualification = '';
        } else if (doctorate_qualification.startsWith('Doctorate of ')) {
          doctorate_qualification = doctorate_qualification.replace('Doctorate of ', '');
        }
        
        setStep3Data({
          qualification: qualification,
          honours_qualification: honours_qualification,
          masters_qualification: masters_qualification,
          doctorate_qualification: doctorate_qualification
        });

        // Pre-fill step 4 data
        setStep4Data({
          branches: data.branches || [],
          subject_category: data.subject_category || 'STEM'
        });

        // Check if registration is under review (status = 1)
        if (data.reg_status === 1) {
          setWizardCompleted(true);
        }
      } catch (error) {
        console.log('Could not fetch tutor data:', error);
      }
    };

    fetchTutorData();
  }, []);

  const handleStep1Change = (field, value) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep1Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep2Change = (field, value) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep2Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep3Change = (field, value) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep3Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep4Change = (field, value) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep4Data(prev => ({ ...prev, [field]: value }));
  };

  const handleStep5Change = (field, value) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setStep5Data(prev => ({ ...prev, [field]: value }));
  };

  // Document upload handlers
  const handleDocumentUploaded = useCallback((docType, filename) => {
    setUploadedDocuments(prev => new Set([...(prev || []), docType]));
    setUploadedFilenames(prev => {
      const newMap = new Map(prev || []);
      newMap.set(docType, [filename]);
      return newMap;
    });
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setDocumentErrors(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
  }, []);

  const handleDocumentUploadFailed = useCallback((docType) => {
    setUploadingDocuments(prev => {
      const newSet = new Set(prev || []);
      newSet.delete(docType);
      return newSet;
    });
    setDocumentErrors(prev => new Set([...(prev || []), docType]));
  }, []);

  const handleUploadStart = useCallback((docType) => {
    setUploadingDocuments(prev => new Set([...(prev || []), docType]));
  }, []);

  const validateDocuments = useCallback(() => {
    if (!uploadedDocuments.has('id_passport')) {
      setDocumentErrors(new Set(['id_passport']));
      return false;
    }
    return true;
  }, [uploadedDocuments]);

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) throw new Error("User ID not found in localStorage.");

      // Combine all data and submit
      // If Not Applicable is checked (empty qualifier), save as 'Fast Tracked', otherwise prepend prefix
      const payload = {
        ...step1Data,
        ...step2Data,
        qualification: step3Data.qualification ? `Bachelor of ${step3Data.qualification}` : 'N/A',
        honours_qualification: step3Data.honours_qualification ? `Honours of ${step3Data.honours_qualification}` : 'N/A',
        masters_qualification: step3Data.masters_qualification ? `Master's of ${step3Data.masters_qualification}` : 'N/A',
        doctorate_qualification: step3Data.doctorate_qualification ? `Doctorate of ${step3Data.doctorate_qualification}` : 'N/A',
        branches: step4Data.branches || [],
        subject_category: step4Data.subject_category || 'STEM',
        ...step5Data
      };

      console.log("Submitting tutor profile:", payload);
      console.log("Branch teaching levels:", step4Data.branchTeachingLevels);
      
      // Update tutor profile
      const profileRes = await axios.post(`/accounts/tutors/${userId}/profile/`, payload);
      if (profileRes.status !== 200 && profileRes.status !== 201) {
        throw new Error("Failed to update tutor profile");
      }

      // Update registration status to 1
      const regStatusPayload = { reg_status: 1 };
      const regStatusRes = await axios.post(`/accounts/tutors/${userId}/update-reg-status/`, regStatusPayload);
      if (regStatusRes.status !== 200) {
        throw new Error("Failed to update registration status");
      }

      // Save branch teaching levels if they exist
      if (step4Data.branchTeachingLevels && Object.keys(step4Data.branchTeachingLevels).length > 0) {
        const branchesWithLevels = Object.entries(step4Data.branchTeachingLevels)
          .filter(([branchId, levels]) => levels && levels.length > 0)
          .map(([branchId, levels]) => ({
            branch_id: parseInt(branchId),
            teaching_levels: levels
          }));

        if (branchesWithLevels.length > 0) {
          console.log("Submitting branch teaching levels:", branchesWithLevels);
          try {
            const branchLevelsRes = await axios.post(`/accounts/tutors/${userId}/branches/update/`, {
              branches: branchesWithLevels
            });
            console.log("Branch levels saved successfully:", branchLevelsRes.data);
          } catch (branchError) {
            console.warn("Failed to save branch teaching levels:", branchError);
            // Don't fail the whole registration for this
          }
        }
      }

      // Update localStorage and dispatch events
      localStorage.setItem('reg_status', '1');
      // Only dispatch wizardCompleted, don't trigger regStatusUpdated to avoid unwanted reload
      window.dispatchEvent(new Event('wizardCompleted'));

      setWizardCompleted(true);

      // Stay on the review screen - don't redirect

      return true;
    } catch (error) {
      console.error("Error submitting tutor profile:", error);
      alert('Failed to complete registration. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when step changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
    return () => clearTimeout(timeout);
  }, [currentStep]);

  if (wizardCompleted) {
    return (
      <div className="wizardbox">
        <CompletionSuccess />
      </div>
    );
  }

  return (
    <>
      <div className="wizardbox">
        <div className="step-indicator mb-4 d-flex justify-content-between align-items-end" style={{ gap: 0 }}>
          {stepLabels.map((step, index) => {
            const stepNum = index + 1;
            const isActive = currentStep === stepNum;
            const isCompleted = currentStep > stepNum;
            
            return (
              <div
                key={stepNum}
                className={`step text-center ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                style={{
                  flex: 1,
                  position: "relative",
                  zIndex: 1,
                  minWidth: 100,
                  cursor: 'default'
                }}
              >
                <div
                  className={`circle d-flex align-items-center justify-content-center mb-2 ${isActive ? "circle-active" : ""} ${isCompleted ? "circle-completed" : ""}`}
                  style={{
                    width: 60,
                    height: 60,
                    margin: "0 auto",
                    borderRadius: "50%",
                    background: isActive
                      ? "linear-gradient(135deg, #296f93 60%, #5bc0de 100%)"
                      : isCompleted
                      ? "linear-gradient(135deg, #28a745 60%, #20c997 100%)"
                      : "#e9ecef",
                    color: isActive || isCompleted ? "#fff" : "#adb5bd",
                    fontSize: 32,
                    boxShadow: isActive
                      ? "0 0 0 4px #296f9333"
                      : isCompleted
                      ? "0 0 0 2px #28a74533"
                      : "none",
                    border: isActive ? "2px solid #296f93" : isCompleted ? "2px solid #28a745" : "2px solid #dee2e6",
                    transition: "all 0.2s"
                  }}
                >
                  <i className={`bi ${step.icon}`} />
                </div>
                <div
                  className="label small fw-bold"
                  style={{
                    color: isActive
                      ? "#296f93"
                      : isCompleted
                      ? "#28a745"
                      : "#6c757d",
                    fontSize: isActive ? 18 : 15,
                    marginTop: 4,
                    letterSpacing: 0.5,
                    transition: "color 0.2s, font-size 0.2s"
                  }}
                >
                  {step.label}
                </div>
                {/* Progress bar connector */}
                {index < stepLabels.length - 1 && (
                  <div
                    style={{
                      position: "absolute",
                      top: 30,
                      right: -50,
                      width: 100,
                      height: 6,
                      background: isCompleted
                        ? "linear-gradient(90deg, #28a745 60%, #20c997 100%)"
                        : "#dee2e6",
                      zIndex: 0,
                      borderRadius: 3,
                      transition: "background 0.2s"
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {currentStep === 1 && (
          <Step1Identification
            step1Data={step1Data}
            handleStep1Change={handleStep1Change}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            nextStep={() => setCurrentStep(2)}
            loading={loading}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleUploadStart={handleUploadStart}
            validateDocuments={validateDocuments}
          />
        )}

        {currentStep === 2 && (
          <Step2Address
            step2Data={step2Data}
            handleStep2Change={handleStep2Change}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            previousStep={() => setCurrentStep(1)}
            nextStep={() => setCurrentStep(3)}
            loading={loading}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            setDocumentErrors={setDocumentErrors}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleUploadStart={handleUploadStart}
          />
        )}

        {currentStep === 3 && (
          <Step3AcademicRecord
            step3Data={step3Data}
            handleStep3Change={handleStep3Change}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            previousStep={() => setCurrentStep(2)}
            nextStep={() => setCurrentStep(4)}
            loading={loading}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleUploadStart={handleUploadStart}
          />
        )}

        {currentStep === 4 && (
          <Step4Subjects
            step4Data={step4Data}
            handleStep4Change={handleStep4Change}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            previousStep={() => setCurrentStep(3)}
            nextStep={() => setCurrentStep(5)}
            loading={loading}
          />
        )}

        {currentStep === 5 && (
          <Step5Banking
            step5Data={step5Data}
            handleStep5Change={handleStep5Change}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            previousStep={() => setCurrentStep(4)}
            onSubmit={handleFinalSubmit}
            loading={loading}
            uploadedDocuments={uploadedDocuments}
            uploadingDocuments={uploadingDocuments}
            uploadedFilenames={uploadedFilenames}
            documentErrors={documentErrors}
            setDocumentErrors={setDocumentErrors}
            handleDocumentUploaded={handleDocumentUploaded}
            handleDocumentUploadFailed={handleDocumentUploadFailed}
            handleUploadStart={handleUploadStart}
          />
        )}
      </div>

      <style>
        {`
          .step-indicator .circle {
            transition: box-shadow 0.2s, border 0.2s, background 0.2s, color 0.2s;
          }
          .step-indicator .circle-active {
            animation: pulse 0.7s;
          }
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 #0d6efd55; }
            70% { box-shadow: 0 0 0 10px #0d6efd22; }
            100% { box-shadow: 0 0 0 0 #0d6efd55; }
          }
        `}
      </style>
    </>
  );
};

export default TutorFormWizard;
